/**
 * compactDegradeDetail — structure-aware, dialect-correct prose for the
 * compact degrade notice (VQE Polish Advisor 2 §B2 + BUG 2 compact half).
 *
 * Turns a live `QueryStructure` (+ the INSERT tail outline, when the reason is
 * INSERT/upsert) into a short headline + supporting facts, so the notice
 * teaches the reader WHAT the statement is instead of showing a codename
 * ("set-operator") or a dialect-leaking generic list. BUG 2: the old
 * `compactDegrade.ts` set-operator message hard-coded "UNION / INTERSECT /
 * EXCEPT" even on Oracle, which spells its third operator MINUS —
 * `setOperatorDetail` below names the operators ACTUALLY used
 * (`QueryStructure.compoundOperators`), spelled per engine via
 * `setOperatorChoicesForEngine`, so this can never leak again.
 *
 * Pure — no React — so every reason is unit-testable in isolation.
 *
 * spec: Ralph/VqePolish/advisors/02-dml-safety-and-polish.md § B2;
 * Ralph/VqePolish/advisors/03-bug-dossier.md § BUG 2.
 */

import type { QualifiedName, SqlEngine } from '../../../sql-grammar/model';
import type {
  CompoundOperator,
  FromItem,
  QueryStructure,
  SelectItem,
} from '../../../sql-resolver/types/queryStructure';
import type { CteBinding } from '../../../sql-resolver/types/scope';
import type { RenderMode } from '../types';
import type { InsertOutline } from '../render/insert/extractInsertOutline';
import { setOperatorChoicesForEngine } from '../render/select/setOperatorKindMaps';
import { ENGINE_DISPLAY_NAME, windowClauseMinVersionHint } from '../dialect/statementCapabilities';
import { vqeCopy } from '../ux/vqeCopy';

export interface DegradeDetail {
  readonly headline: string;
  readonly facts: readonly string[];
}

function formatQualifiedName(q: QualifiedName): string {
  return q.raw && q.raw.length > 0 ? q.raw : q.name;
}

/** Shared with `compactDegrade.ts` so the classify + detail cascades never disagree. */
export function hasWindowFunction(structure: QueryStructure): boolean {
  return (structure.selectItems ?? []).some((item) => item.window !== undefined);
}

/**
 * ANA-13/17/19 — the NAMED-window half of the window split. True iff the
 * statement carries a shape the compact surface has NO editable home for: a
 * window item that references a NAMED window (`OVER w`) OR a standalone
 * `WINDOW w AS (…)` clause (`NamedWindowSection` is imported only by the full
 * `SelectRenderer`, never by `CompactEditSections`). An INLINE `OVER (…)` window
 * returns false here — it is self-contained in one SELECT chip, so it degrades
 * PER-COLUMN (the chip locks) instead of degrading the whole query. This gates
 * the compact degrade decision (`compactDegrade.ts`) and the detail cascade
 * below, so the two never disagree about which reason wins.
 */
export function hasNamedWindowFunction(structure: QueryStructure): boolean {
  if ((structure.windows?.length ?? 0) > 0) return true;
  return (structure.selectItems ?? []).some((item) => Boolean(item.window?.namedWindow));
}

/** Shared with `compactDegrade.ts` so the classify + detail cascades never disagree. */
export function hasDerivedTableFrom(structure: QueryStructure): boolean {
  return (structure.fromItems ?? []).some((item) => item.target.kind === 'derived-table');
}

/** True when any FROM entry is a GRAPH_TABLE invocation (SQL/PGQ). Checked
 *  structurally via the from-item's `function-call` kind + an unqualified,
 *  case-insensitive name match on `routine` — the SAME test `aliasScope.ts`
 *  uses to retag a GRAPH_TABLE target as `source: 'graph-table'` (never a
 *  real relation name comparison, so a genuine table/view named
 *  `graph_table` referenced *without* a call — `FROM graph_table` — is a
 *  `'relation'` target and correctly does NOT match here).
 *
 *  The FROM extractor classifies `name(` as a function-call purely from the
 *  character immediately following the identifier — independent of whether
 *  the call's parens ever balance (see `extractQueryStructure.from.ts`'s
 *  `readFromItem`) — so an in-flight/unclosed `GRAPH_TABLE(g MATCH (a` still
 *  resolves to `{ kind: 'function-call', routine: 'GRAPH_TABLE' }` and this
 *  structural check already degrades it; no separate raw-text rescan is
 *  needed (or possible — `QueryStructure` carries no raw source text at
 *  this layer).
 *
 *  Also scans `structure.joins[].right` — an explicit `JOIN GRAPH_TABLE(...)
 *  ON ...` target lives in `JoinClause.right`, not `fromItems`
 *  (`extractQueryStructure.from.ts`'s `readJoinClause` builds the JOIN's
 *  right-hand side via the SAME `readFromItem` as the comma-separated FROM
 *  list, so it produces the identical `{ kind: 'function-call', routine }`
 *  shape — the check below reuses that shape, just against the other list). */
function isGraphTableTarget(item: FromItem): boolean {
  return (
    item.target.kind === 'function-call' &&
    !item.target.routine.schema &&
    item.target.routine.name.toUpperCase() === 'GRAPH_TABLE'
  );
}

export function hasGraphTableFrom(structure: QueryStructure): boolean {
  if ((structure.fromItems ?? []).some(isGraphTableTarget)) return true;
  return (structure.joins ?? []).some((join) => isGraphTableTarget(join.right));
}

// ---- CTE --------------------------------------------------------------

function cteDetail(ctes: readonly CteBinding[]): DegradeDetail {
  const n = ctes.length;
  const names = ctes.map((c) => formatQualifiedName(c.name));
  const recursiveCount = ctes.filter((c) => c.isRecursive).length;
  const recursiveNote =
    recursiveCount === 0 ? '' : recursiveCount === n ? ' (recursive)' : ` (${recursiveCount} recursive)`;
  return {
    headline: `Defines ${n} CTE${n === 1 ? '' : 's'}${recursiveNote}: ${names.join(' → ')}`,
    facts: [],
  };
}

// ---- set operator (BUG 2 — the actual compact-half fix) ----------------

function operatorLabel(op: CompoundOperator, engine: SqlEngine): string {
  const choices = setOperatorChoicesForEngine(engine);
  const match = choices.find((c) => c.kind === op.kind && c.all === op.all);
  return match ? match.label : `${op.kind}${op.all ? ' ALL' : ''}`;
}

function setOperatorDetail(structure: QueryStructure, engine: SqlEngine): DegradeDetail {
  const branchCount = 1 + (structure.compoundBranches?.length ?? 0);
  const operators = structure.compoundOperators ?? [];
  // `compoundOperators[i].kind` already records the LITERAL keyword parsed
  // from source (MINUS is only ever recognized on Oracle — see
  // `selectShape.ts` `isSetOp`), so this is inherently dialect-correct; running
  // it through `setOperatorChoicesForEngine` on top gives the exact display
  // label (e.g. "UNION ALL") the set-operator dropdown itself uses, so the
  // degrade notice and the dropdown never describe the same operator two
  // different ways.
  const labels = Array.from(new Set(operators.map((op) => operatorLabel(op, engine))));
  const opText = labels.length > 0 ? labels.join(' / ') : 'a set operator';
  return {
    headline: `Combines ${branchCount} SELECTs with ${opText}`,
    facts: [],
  };
}

// ---- window function ----------------------------------------------------

function formatWindowItem(item: SelectItem): string {
  return item.alias ? `${item.expressionText} AS ${item.alias}` : item.expressionText;
}

// ANA-10: when a NAMED window is present, mirror full-mode NamedWindowSection
// (`render/select/NamedWindowSection.tsx`) and surface the same truthful,
// engine-version hint — mssql (2022+) / oracle (21c+) only, via the shared
// `windowClauseMinVersionHint` + `vqeCopy.capability.window.versionHint`
// formatter (zero new copy here). `undefined` on pg/mysql/sqlite (never
// version-gated there) and on inline-`OVER`-only statements (no WINDOW
// clause, so no version claim to make).
function windowDetail(structure: QueryStructure, engine: SqlEngine): DegradeDetail {
  const items = (structure.selectItems ?? []).filter((item) => item.window !== undefined);
  if (items.length === 0) return { headline: 'Uses a window function', facts: [] };

  const named = items.filter((item) => item.window?.namedWindow);
  if (named.length > 0) {
    const names = Array.from(new Set(named.map((item) => item.window!.namedWindow!)));
    const nameText = names.map((name) => `\`${name}\``).join(', ');
    const inline = items.filter((item) => !item.window?.namedWindow).map(formatWindowItem);
    const minVersion = windowClauseMinVersionHint(engine);
    const facts = minVersion
      ? [...inline, vqeCopy.capability.window.versionHint(ENGINE_DISPLAY_NAME[engine], minVersion)]
      : inline;
    return {
      headline: `${named.length} window function${named.length === 1 ? '' : 's'} over named window ${nameText}`,
      facts,
    };
  }

  const [first, ...rest] = items;
  return {
    headline: `Uses ${formatWindowItem(first)}`,
    facts: rest.map(formatWindowItem),
  };
}

// ---- derived-table subquery in FROM -------------------------------------

function subqueryDetail(structure: QueryStructure): DegradeDetail {
  const derived = (structure.fromItems ?? []).find((item) => item.target.kind === 'derived-table');
  const alias = derived?.alias?.alias;
  return {
    headline: alias
      ? `Nests a derived-table subquery in FROM, aliased \`${alias}\``
      : 'Nests a derived-table subquery in FROM',
    facts: [],
  };
}

// ---- GRAPH_TABLE (SQL/PGQ) ----------------------------------------------

function graphTableDetail(): DegradeDetail {
  return {
    headline: 'Graph queries (GRAPH_TABLE) can’t be edited visually yet — edit as SQL.',
    facts: [],
  };
}

// ---- INSERT / upsert ------------------------------------------------------

function insertDetail(structure: QueryStructure | null, outline: InsertOutline | null): DegradeDetail {
  if (!outline || !outline.hasInsert || outline.targetText === null) {
    return { headline: 'INSERT statements are edited in the full editor.', facts: [] };
  }
  const target = outline.targetText;
  const columnsText =
    outline.columns && outline.columns.length > 0 ? ` (${outline.columns.join(', ')})` : '';

  // MySQL upsert — ON DUPLICATE KEY UPDATE always updates (no DO-NOTHING form).
  if (outline.onDuplicateStart !== null) {
    return {
      headline: `INSERT into ${target}${columnsText} … ON DUPLICATE KEY UPDATE`,
      facts: ['Updates existing rows on key collision.'],
    };
  }
  // PG upsert — ON CONFLICT DO UPDATE / DO NOTHING. The outline only carries
  // the raw clause span; the action (update vs. nothing) comes from the
  // resolver's `structure.onConflict.action` when available.
  if (outline.onConflictStart !== null) {
    const isDoNothing = structure?.onConflict?.action === 'do-nothing';
    return {
      headline: `INSERT into ${target}${columnsText} … ON CONFLICT ${isDoNothing ? 'DO NOTHING' : 'DO UPDATE'}`,
      facts: [
        isDoNothing
          ? 'Skips the row when the key already exists.'
          : 'Updates existing rows on key collision.',
      ],
    };
  }
  if (outline.bodyKind === 'values') {
    const n = outline.valuesRows.length;
    return { headline: `INSERT ${n} row${n === 1 ? '' : 's'} into ${target}${columnsText}`, facts: [] };
  }
  if (outline.bodyKind === 'select') {
    return { headline: `INSERT into ${target}${columnsText} from a SELECT`, facts: [] };
  }
  if (outline.bodyKind === 'default-values') {
    return { headline: `INSERT DEFAULT VALUES into ${target}`, facts: [] };
  }
  return { headline: `INSERT into ${target}${columnsText}`, facts: [] };
}

// ---- MERGE ----------------------------------------------------------------

function formatTargetName(item?: FromItem): string {
  if (!item) return 'the target table';
  const t = item.target;
  if (t.kind === 'relation') return formatQualifiedName(t.qualified);
  if (t.kind === 'cte-ref') return formatQualifiedName(t.cte.name);
  if (t.kind === 'function-call') return formatQualifiedName(t.routine);
  return 'a subquery';
}

function mergeDetail(structure: QueryStructure): DegradeDetail {
  const target = formatTargetName(structure.mergeTarget);
  const matched = structure.mergeWhenMatched?.length ?? 0;
  const notMatched = structure.mergeWhenNotMatched?.length ?? 0;
  const total = matched + notMatched;
  const facts: string[] = [];
  if (matched > 0) facts.push(`${matched} WHEN MATCHED branch${matched === 1 ? '' : 'es'}`);
  if (notMatched > 0) facts.push(`${notMatched} WHEN NOT MATCHED branch${notMatched === 1 ? '' : 'es'}`);
  return {
    headline: `MERGEs into ${target} with ${total} WHEN branch${total === 1 ? '' : 'es'}`,
    facts,
  };
}

// ---- generic fallback -------------------------------------------------

function genericFallback(mode: RenderMode | null): DegradeDetail {
  if (mode === 'insert') return { headline: 'INSERT statements are edited in the full editor.', facts: [] };
  if (mode === 'merge') return { headline: 'MERGE statements are edited in the full editor.', facts: [] };
  return { headline: 'This statement is edited in the full editor.', facts: [] };
}

/**
 * Builds the rich degrade detail for the compact popover. Re-derives which
 * structural shape triggered the degrade using the SAME precedence order as
 * `classifyCompactDegrade` (mode-gated INSERT/MERGE/unsupported first, then
 * structure-gated CTE → set-operator → window-function → graph-table →
 * subquery) so the two never disagree about which reason "wins" when a
 * statement matches more than one shape. `insertOutline` is only consulted for `mode === 'insert'`
 * (computed by the caller via `extractInsertOutline` — the same token-walker
 * the full-mode `InsertRenderer` already uses, for target/columns/row-count/
 * body-kind consistency between compact and full).
 */
export function buildDegradeDetail(
  structure: QueryStructure | null,
  insertOutline: InsertOutline | null,
  engine: SqlEngine,
  mode: RenderMode | null,
): DegradeDetail {
  if (mode === 'insert') return insertDetail(structure, insertOutline);
  if (mode === 'merge') return structure ? mergeDetail(structure) : genericFallback(mode);
  if (!structure) return genericFallback(mode);
  if ((structure.ctes?.length ?? 0) > 0) return cteDetail(structure.ctes!);
  if ((structure.compoundBranches?.length ?? 0) > 0) return setOperatorDetail(structure, engine);
  // Mirror classify's precedence: only a NAMED window degrades (inline OVER
  // locks per-column). An inline OVER alongside a derived-table FROM therefore
  // falls through to `subqueryDetail`, so the reason + message agree.
  if (hasNamedWindowFunction(structure)) return windowDetail(structure, engine);
  if (hasGraphTableFrom(structure)) return graphTableDetail();
  if (hasDerivedTableFrom(structure)) return subqueryDetail(structure);
  return genericFallback(mode);
}
